import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DocketService } from 'src/app/api/core/docket/docket.service';
import { WatcherService } from 'src/app/api/utils/watcher/watcher.service';
import { CONTEXT_TYPE, Order } from 'src/app/models/order.model';
import { CommsService } from '../../../../utils/comms/comms.service';
import { APP_DOCKET_DISPLAY_UPDATE } from '../../../../utils/watcher/watcher-event';
import { AuthService } from '../../../auth/auth.service';
import { CoreService } from '../../../core/core.service';
import { IFilterObject } from '../../../docket-filter/docket-filter.service';
import { FeatureFlagList, FeatureFlagsService } from '../../../settings/feature-flags.service';
import { HandleFailedDispatchService } from '../handle.failed.dispatch.service';
import { BffDispatchRequest, OrderDispatcherService } from '../order-dispatcher/order-dispatcher.service';

import find from 'lodash/find';
import size from 'lodash/size';
import { take, timeout } from 'rxjs/operators';
import { AnalyticsService } from 'src/app/api/utils/analytics/analytics.service';
import { BffService } from 'src/app/api/utils/comms/bff/bff.service';
import {
  OMBffSocketEventType,
  SocketIOEvent,
  SocketioService,
} from 'src/app/api/utils/comms/socketio/socketio.service';
import { GeneratedDocketRepositoryService } from '../../repository/generated-docket-repository.service';

@Injectable({
  providedIn: 'root',
})
export class DispatchEtaService extends OrderDispatcherService {
  constructor(
    public watcher: WatcherService,
    public comms: CommsService,
    public auth: AuthService,
    public core: CoreService,
    public docketService: DocketService,
    public handleFailedDispatchService: HandleFailedDispatchService,
    public featureFlagsService: FeatureFlagsService,
    public socketIO: SocketioService,
    public generatedDocketRepo: GeneratedDocketRepositoryService,
    public analytics: AnalyticsService,
    public bffService: BffService,
  ) {
    super(core, docketService, auth, bffService);
  }

  /**
   * Enable the order's pending status to update the UI of pending ETA request
   * @param order - The order to update
   */
  prepareOrderForETAUpdate(order: Order, eta: string) {
    order.eta = eta;

    let markAsPending: boolean = false;
    if (order.context_type == CONTEXT_TYPE.Dine_in) {
      const { bump_status } = this.docketService.filterService.getSelectedFilters();
      const bumpFilter: IFilterObject = find(bump_status, 'toggled');
      markAsPending = bumpFilter && bumpFilter.data == 'new';
    }

    order.setPending(markAsPending);

    // force a list update
    this.watcher.broadcast(APP_DOCKET_DISPLAY_UPDATE, 'updateDocketEta');
    this.docketService.orderFilterCountSummary();
  }

  /**
   * Create the request body for updating the ETA in Drupal
   * @param order - The order to be updated
   * @param eta - The ETA to set in ISO format
   * @returns - The request object body
   */
  createRequestBody(order: Order, eta: string) {
    const type: string = order.type == 'batch_order' ? order.type : null;

    let retVal: any = {
      bump_screen_id: this.auth.Active_bump_screen.id,
      return_orders: true, // force it true for now - it needs the new docket with ETA
      type,
      orders: [],
    };

    if (size(type) && type == 'batch_order') {
      retVal.type = type;
      retVal.batch_id = order.id;
      retVal.eta = eta;
    } else {
      retVal.orders.push({
        id: order.id,
        eta: eta,
      });
    }

    return retVal;
  }

  /**
   * Send a request to the server to update the order ETA
   * @param eta - The ETA to set
   * @param docketId - The ID of the order to be updated
   * @param action -
   * @param returnOrders - Flagger to return all orders as part of payload request
   * @param showErrorMessage - Show error message on failure
   */
  updateDocketEta(eta: string, docketId: string, showErrorMessage: boolean = true) {
    return new Promise(etaResolver => {
      const order: Order = this.docketService.getOrderById(docketId);
      if (order) {
        const method: string = 'confirm';

        this.prepareOrderForETAUpdate(order, eta);

        // setup object to send - with bump screen ID, return order obj :NO, & ETA array
        let sendObj: any = this.createRequestBody(order, eta);

        const triggerDate = new Date();
        this.updateOrder_Drupal(sendObj)
          .pipe(take(1))
          .subscribe(
            payload => {
              // block propagation of response if user already logged out
              if (!this.auth.Logged_in) {
                etaResolver(false);
                return;
              }

              if (payload) {
                const { success, result, orders } = payload;
                if (success && size(result)) {
                  // if the backend returned new orders, save it
                  this.docketService.updateOrders(payload.orders, false);

                  // handle ETA already saved
                  if (order.id == result[0].id && !result[0].success) {
                    this._handleFailedResponse(order, method, showErrorMessage, result);

                    etaResolver(false);
                    return;
                  }

                  // ALL WENT WELL
                  if (this.featureFlagsService.isFeatureFlagEnabled(FeatureFlagList.BFF)) {
                    const triggerBFFDate = new Date();

                    // request the docket from Docket service
                    this.updateOrder_Bff(order)
                      .pipe(take(1))
                      .subscribe(
                        docketReceived => {
                          if (docketReceived) {
                            this.analytics.trackEvent(
                              'ETA Request',
                              `BFF:Success:${order.id}:${new Date().valueOf() - triggerBFFDate.valueOf()}`,
                              this.auth.Tracking_key,
                            );
                          }

                          // success for BFF
                          etaResolver(true);
                        },
                        fail => {
                          console.log('Failed BFF ETA:', fail);

                          this.analytics.trackEvent(
                            'ETA Request',
                            `BFF:Failed (${fail ? fail.message : ''}):${order.id}:${
                              new Date().valueOf() - triggerBFFDate.valueOf()
                            }`,
                            this.auth.Tracking_key,
                          );

                          // return a TRUE to still trigger the print using Drupal dockets
                          etaResolver(true);
                        },
                      );
                  } else {
                    this.analytics.trackEvent(
                      'ETA Request',
                      `Drupal:Success:${order.id}:${new Date().valueOf() - triggerDate.valueOf()}`,
                      this.auth.Tracking_key,
                    );

                    // success for DRUPAL
                    etaResolver(true);
                  }
                } else {
                  // update failed - success: false
                  this._handleFailedResponse(order, method, showErrorMessage, result);

                  etaResolver(false);
                }
              } else {
                // update failed - no payload
                this._handleFailedResponse(order, method, showErrorMessage, null);
                etaResolver(false);
              }
            },
            fail => {
              // block propagation of response if user already logged out
              if (!this.auth.Logged_in) {
                etaResolver(false);
                return;
              }

              this._handleFailedResponse(order, method, showErrorMessage, null);
              etaResolver(false);
            },
          );
      } else {
        etaResolver(false);
      }
    });
  }

  /**
   * Handles failed responses and reset the docket to its previous state
   * @param order The order to reset
   * @param method The request/action made
   * @param showErrorMessage Flagger to show an error message
   * @param result Optional. The value to use when resetting the order value
   */
  private _handleFailedResponse(order: Order, method: string, showErrorMessage: boolean, result: any) {
    if (showErrorMessage) {
      const errorMessage: string = result ? result[0].error : null;
      const resultETA: string = result ? result[0].eta : null;

      this.handleFailedDispatchService.handleFailedUpdateRequest(order, method, errorMessage, resultETA);
    } else {
      this.docketService.resetDocket(order.id, 'eta', result ? result[0].eta : null);
    }
  }

  /**
   * Send HTTP requests to Drupal
   * @param sendObj The request body
   * @returns Observable
   */
  updateOrder_Drupal(sendObj: any): Observable<any> {
    return new Observable(subscriber => {
      const header = new HttpHeaders(this.auth.headerData);

      // force 20 seconds timeout for setting ETA
      this.comms.post(
        this.core.Active_server.url + 'api/bump/orders/v3',
        sendObj,
        {
          headers: header,
        },
        payload => {
          subscriber.next(payload);
          subscriber.complete();
        },
        err => {
          subscriber.error(err);
          subscriber.complete();
        },
        this.UPDATE_TIMEOUT,
      );
    });
  }

  /**
   * Send HTTP requests to BFF service
   * @param order The order to update
   * @returns Observable
   */
  updateOrder_Bff(order: Order) {
    return new Observable(subscribe => {
      // listen to the generated docket
      this.socketIO
        .on<SocketIOEvent>(OMBffSocketEventType.DOCKET, order.id)
        .pipe(timeout(3000), take(1))
        .subscribe(
          (docketData: any) => {
            this.generatedDocketRepo.addDocketToRepository(docketData);
            subscribe.next(true);
            subscribe.complete();
          },
          fail => {
            subscribe.error(fail);
            subscribe.complete();
          },
        );

      this.analytics.trackEvent('Sending ETA Update', `${order.id}, Start: ${Date.now()}`, this.auth.Tracking_key);
      this.dispatchDocket([order.id], BffDispatchRequest.ETA)
        .pipe(take(1))
        .subscribe(
          () => {
            this.analytics.trackEvent('BFF ETA', `Success: ${order.id}`, this.auth.Tracking_key);
          },
          () => {
            this.analytics.trackEvent('BFF ETA', `Failed: ${order.id}`, this.auth.Tracking_key);
            subscribe.error(false);
            subscribe.complete();
          },
        );
    });
  }
}
